import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { useDeletePasskey } from "@/hooks/auth/usePasskeyQueries";

/**
 * DeletePasskeyDialog
 *
 * Confirmation dialog shown before permanently deleting a passkey.
 * Warns the user the action cannot be undone and shows the passkey's
 * friendly name so they know exactly what they're removing.
 *
 * Props:
 *   open         {boolean}  — controlled open state
 *   onOpenChange {function} — called when dialog should open/close
 *   passkey      {object}   — the passkey to delete { id, friendlyName }
 */
export default function DeletePasskeyDialog({ open, onOpenChange, passkey }) {
    const { mutate: deletePasskey, isPending } = useDeletePasskey();

    const handleConfirm = () => {
        if (!passkey?.id) return;

        deletePasskey(passkey.id, {
            onSuccess: () => onOpenChange(false),
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="overflow-hidden rounded-3xl border border-gray-200 p-0 shadow-2xl sm:max-w-md">
                <div className="border-b border-gray-100 bg-white px-6 py-5 sm:px-7 sm:py-6">
                    <DialogHeader className="space-y-2">
                        <DialogTitle className="text-xl font-bold tracking-tight text-gray-900">
                        Remove passkey?
                        </DialogTitle>
                        <DialogDescription className="text-sm leading-6 text-gray-500 pt-5">
                            You are about to remove{" "}
                            <span className="font-semibold text-gray-800">
                                {passkey?.friendlyName ?? "this passkey"}
                            </span>
                            . This action cannot be undone. If this is your only passkey,
                            you will no longer be able to sign in with biometrics or a
                            security key.
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <DialogFooter className="flex flex-col-reverse gap-3 bg-gray-50 px-6 py-5 sm:flex-row sm:justify-end sm:px-7">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isPending}
                        className="cursor-pointer rounded-full px-5">
                        Cancel
                    </Button>

                    <Button
                        type="button"
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={isPending}
                        className="cursor-pointer flex items-center gap-2 rounded-full px-5">
                        {isPending ? (
                            <>
                                <Spinner className="h-4 w-4 text-white" />
                                <span>Removing...</span>
                            </>
                        ) : (
                            "Remove passkey"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}